import { useMutation, useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { issuesKey, issueSummaryKey } from '../scope';
import { getRuntimeConfig } from '../../config/runtime-config';
import { adaptIssueList, type IssueViewModel } from '../issues-types';

export interface AnalysisResult {
  likelyCause: string;
  suggestedFix: string;
  confidence: string;
  references: string[];
}

/**
 * Fetch the unified single-pane issues list for the active site from
 * `/api/v1/sites/{site}/issues` (`backend/api_server/api/issues.py`)
 * — task failures, client errors, worker health, and device faults
 * (unioned server-side from `Alert`), already merged and sorted by
 * last activity.
 */
export function useIssues(source?: string) {
  const siteId = useSafeSiteId();
  return useQuery<IssueViewModel[]>({
    queryKey: issuesKey(siteId, source),
    queryFn: async () => {
      if (!siteId) return [];
      const { getRequestBearerToken } = await import('../../auth/request-auth');
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const params = source ? `?source=${encodeURIComponent(source)}` : '';
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/issues${params}`;
      const res = await fetch(url, { cache: 'no-store', headers });
      const contentType = res.headers.get('content-type') ?? '';
      if (!res.ok || !contentType.includes('application/json')) {
        return [];
      }
      return adaptIssueList(await res.json());
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/** Per-status issue counts from `GET .../issues/summary`. */
export interface IssueSummary {
  open: number;
  in_progress: number;
  resolved: number;
}

/**
 * `GET /api/v1/sites/{site}/issues/summary` — real server-side
 * counts grouped by triage status, for the top-bar badge. Replaces
 * counting `useIssues().data.length`, which was the length of an
 * UNFILTERED page capped at the server's default limit: it counted
 * resolved rows as "open" and saturated at exactly the page size.
 *
 * `sources` mirrors the endpoint's CSV `?sources=` filter so the
 * badge can request exactly the source set the viewer's Support
 * screen shows (ordinary operators are pinned to `task` there).
 * The `device` source is excluded server-side regardless — device
 * problems are Alert rows and are already counted by the Alerts
 * badge; counting them here too double-reported every device fault.
 *
 * 15 s poll (the alerts-badge cadence, not this file's 30 s list
 * cadence) so the two badges tick together.
 */
export function useIssueSummary(sources?: readonly string[]) {
  const siteId = useSafeSiteId();
  return useQuery<IssueSummary>({
    queryKey: issueSummaryKey(siteId, sources),
    queryFn: async () => {
      const empty: IssueSummary = { open: 0, in_progress: 0, resolved: 0 };
      if (!siteId) return empty;
      const { getRequestBearerToken } = await import('../../auth/request-auth');
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const params =
        sources && sources.length > 0
          ? `?sources=${encodeURIComponent([...sources].sort().join(','))}`
          : '';
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/issues/summary${params}`;
      const res = await fetch(url, { cache: 'no-store', headers });
      const contentType = res.headers.get('content-type') ?? '';
      if (!res.ok || !contentType.includes('application/json')) return empty;
      const body = (await res.json()) as Partial<IssueSummary>;
      return {
        open: typeof body.open === 'number' ? body.open : 0,
        in_progress: typeof body.in_progress === 'number' ? body.in_progress : 0,
        resolved: typeof body.resolved === 'number' ? body.resolved : 0,
      };
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

/**
 * On-demand AI analysis for a single issue
 * (`POST /api/v1/sites/{site}/issues/{id}/analyze`) — triggered only
 * by an explicit "Analyze with AI" click, never automatically.
 */
export function useAnalyzeIssue() {
  const siteId = useSafeSiteId();
  return useMutation<AnalysisResult, Error, string>({
    mutationFn: async (issueId: string) => {
      const { getRequestBearerToken } = await import('../../auth/request-auth');
      const token = getRequestBearerToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}/issues/${encodeURIComponent(issueId)}/analyze`;
      const res = await fetch(url, { method: 'POST', headers, cache: 'no-store' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail?.message ?? `Analysis failed (${res.status})`);
      }
      return (await res.json()) as AnalysisResult;
    },
  });
}
